import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MessageCircle, Send } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

interface ChallengeCommentsProps {
  challengeId: string;
}

export function ChallengeComments({ challengeId }: ChallengeCommentsProps) {
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState("");

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: comments, isLoading } = useQuery({
    queryKey: ['challenge-comments', challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('challenge_comments')
        .select('*')
        .eq('challenge_id', challengeId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const createCommentMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('challenge_comments')
        .insert({
          challenge_id: challengeId,
          user_id: user.id,
          content,
        });

      if (error) throw error;

      // Create activity entry
      await supabase
        .from('challenge_activity')
        .insert({
          challenge_id: challengeId,
          user_id: user.id,
          activity_type: 'comment',
          activity_data: { preview: content.substring(0, 50) }
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenge-comments', challengeId] });
      queryClient.invalidateQueries({ queryKey: ['challenge-activity', challengeId] });
      setNewComment("");
      toast.success("Comment posted!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to post comment");
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase
        .from('challenge_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenge-comments', challengeId] });
      toast.success("Comment deleted");
    },
  });

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5" />
        <h3 className="text-base sm:text-lg font-semibold">Comments ({comments?.length || 0})</h3>
      </div>

      {/* New Comment */}
      <div className="mb-4 sm:mb-6">
        <Textarea
          placeholder="Share encouragement..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="mb-2 text-sm"
          rows={2}
        />
        <Button
          onClick={() => createCommentMutation.mutate(newComment)}
          disabled={!newComment.trim() || createCommentMutation.isPending}
          className="gap-2 h-9 text-sm"
          size="sm"
        >
          <Send className="h-3.5 w-3.5" />
          Post
        </Button>
      </div>

      {/* Comments List */}
      <div className="space-y-3 sm:space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading comments...</p>
        ) : comments && comments.length > 0 ? (
          comments.map((comment) => {
            const isOwn = comment.user_id === user?.id;
            
            return (
              <div key={comment.id} className="flex gap-2 sm:gap-3">
                <Avatar className="h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                  <AvatarFallback className="text-xs sm:text-sm">
                    {isOwn ? 'You' : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{isOwn ? 'You' : 'User'}</span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm break-words">{comment.content}</p>
                  {isOwn && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteCommentMutation.mutate(comment.id)}
                      disabled={deleteCommentMutation.isPending}
                      className="mt-1 h-6 px-2 text-xs"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            No comments yet. Be the first!
          </p>
        )}
      </div>
    </Card>
  );
}
